import type { DemoSessionActivity } from '@carboncopyco/data';
import { TextBase, TextCaption, TooltipBubble } from '@carboncopyco/ui';
import { secondsToHHmmss } from '@carboncopyco/utils';
import type { Col } from '../../../FixedTable';
import { ViewedScreens } from '../../../InfoPage/DemoPage/ActivitiesTab/ViewedScreens';
import { AssetNameLink } from '../../AssetNameLink';
import type { DashboardProps } from '../../Dashboard';
import { TimestampCol } from '../../SessionBreakdownComponents';

export const VisitsTableCols: (
  demoPagePath: DashboardProps['demoPagePath'],
  playbookPagePath: DashboardProps['playbookPagePath']
) => Col<DemoSessionActivity>[] = (demoPagePath, playbookPagePath) => [
  {
    alignment: 'left',
    title: 'Date & time',
    width: 150,
    maxContentWidth: 130,
    render: ({ timestamp }) => <TimestampCol timestamp={timestamp} />,
  },
  {
    alignment: 'left',
    title: 'Visitor',
    width: 150,
    maxContentWidth: 120,
    render: ({ user }) => (
      <div className="flex flex-col h-10 justify-center">
        <TooltipBubble
          title={user?.name}
          placement="top-start"
          disabled={!user?.name}
        >
          <div className="truncate">
            <TextBase
              label={user?.name || 'Anonymous'}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </TooltipBubble>
        <TooltipBubble
          title={user?.email}
          placement="top-start"
        >
          <div className="truncate text-ds-natural">
            <TextCaption
              label={user?.email}
              ellipsis
            />
          </div>
        </TooltipBubble>
      </div>
    ),
  },
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 150,
    maxContentWidth: 120,
    render: ({ forClient }) => {
      const internalClientNames = ['internal', 'anyone'];
      const opportunity = internalClientNames.includes(forClient)
        ? ''
        : forClient;
      return (
        <TooltipBubble
          title={opportunity}
          placement="top-start"
          disabled={!opportunity}
        >
          <div className="truncate">
            <TextBase
              label={opportunity}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </TooltipBubble>
      );
    },
  },
  {
    alignment: 'left',
    title: 'Demo name',
    width: 200,
    render: ({ asset }) => {
      const { assetType, name, assetId, workspaceId } = asset;
      return (
        <AssetNameLink
          to={
            assetType === 'playbook'
              ? playbookPagePath(assetId, workspaceId)
              : demoPagePath(assetId, workspaceId)
          }
          label={name}
          asset={asset}
          assetType="demo"
        />
      );
    },
  },
  {
    alignment: 'left',
    title: 'Link name',
    width: 200,
    maxContentWidth: 170,
    render: ({ share }) => (
      <TooltipBubble
        title={share.shareName}
        placement="top-start"
      >
        <div className="truncate">
          <TextBase
            label={share.shareName}
            color="text-ds-natural-600"
            ellipsis
          />
        </div>
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: 'Duration',
    width: 120,
    render: (presentation) => (
      <TextBase
        label={secondsToHHmmss(presentation.durationInSeconds)}
        color="text-ds-natural-600"
      />
    ),
  },
  {
    alignment: 'right',
    title: 'Visited screens',
    width: 130,
    render: ({ sessionId, workspaceId, organizationId, screensVisited }) => (
      <ViewedScreens
        sessionId={sessionId}
        workspaceId={workspaceId}
        organizationId={organizationId}
        screensVisited={screensVisited}
      />
    ),
  },
];
